import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { Button, message, Modal, Splitter } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

// 通用组件
import ContainerPanel from "@ms/shared-components/containers/ContainerPanel";
import ContainerModal from "@ms/shared-components/containers/ContainerModal";
import CompsTree, {
  TreeNodeData as CompsTreeNodeData,
} from "@ms/shared-components/comps/CompsTree";
import CompsList from "@ms/shared-components/comps/CompsList";
import CompsDescription from "@ms/shared-components/comps/CompsDescription";
import CompsForm from "@ms/shared-components/comps/CompsForm";

// 本地组件和配置
import { ServiceRecord } from "./service-apis/serviceTypes";
import type { TreeNodeData } from "@ms/shared-components/comps/CompsTree";
import {
  SERVICE_LIST_PANEL_CONFIG,
  SERVICE_DETAIL_PANEL_CONFIG,
  SERVICE_LIST_CONFIG,
  getServiceListButtons,
} from "./config/panelTreeConfig";
import {
  SERVICE_DETAIL_TABS,
  PERMISSION_TREE_CONTAINER_STYLE,
  ADMIN_TAB_CONTAINER_STYLE,
  EMPTY_STATE_CONFIG,
  ADMIN_PANEL_CONFIG,
  ADMIN_GROUP_PANEL_CONFIG,
  getTabButtons,
} from "./config/panelDetailConfig";

// 自定义 Hooks
import { useServiceManagementLogic } from "./hooks/useServiceManagementLogic";
import { useServiceDetail } from "./hooks/useServiceDetail";
import { useServiceDataTransformers } from "./hooks/useServiceDataTransformers";
import { createServiceFormFields } from "./hooks/useServiceFormConfig";
import { useServiceForm } from "./hooks/useServiceForm";
import { ServiceApiService } from "./service-apis/serviceManagementService";
// 服务配置
import {
  FORM_MODAL_CONFIG,
  createServiceModalFooterButtons,
  editServiceModalFooterButtons,
} from "./config/modalServiceConfig";

// 复用 operations 页的权限树与CRUD
import { convertPermissionsToTree } from "../operations/config/panelTreeConfig";
import type { Operation } from "../operations/services/operationService";
import { usePermissionManagement } from "../operations/hooks/usePermissionManagement";
import { usePermissionHandlers } from "../operations/hooks/usePermissionHandlers";
import { usePermissionFormConfig, createModalConfig, editModalConfig } from "../operations/config/modalFormConfig";

interface ServiceManagementProps {
  availableAudiences?: string[];
}

const ServiceManagement: React.FC<ServiceManagementProps> = ({ availableAudiences = [] }) => {
  // 使用自定义 Hook 管理所有状态和逻辑
  const {
    // 状态
    state,
    serviceList,
    loading,
    submitting,
    tokenSubmitting,
    tokenInfo,
    tokenFormRef,

    // 方法
    handleCreate,
    handleEdit,
    handleToggleStatus,
    handleDelete,
    handleTokenFormSubmit,
    handleHealthCheck,
    handleViewPermissions,

    // 状态更新方法
    updateState,
    setTokenFormRef,
    getEditFormInitialValues,

    // Token 相关方法
    copyToken,
    refreshToken,
    deleteToken,
    setServiceList,
  } = useServiceManagementLogic();

  // 从state中获取模态框状态
  const { createModalVisible, editModalVisible, currentRecord } = state;

  // 选中的服务
  const [selectedService, setSelectedService] = useState<ServiceRecord | null>(null);
  // 当前激活的tab
  const [activeTab, setActiveTab] = useState<string>("basic");

  // 使用 useServiceDetail hook 管理服务详情相关的数据
  const {
    serviceDetail,
    permissions,
    administrators,
    adminGroups,
    detailLoading,
    refreshServiceDetail,
    refreshPermissions,
  } = useServiceDetail(selectedService);

  // ============ 权限树（页面->操作）状态 ============
  const [selectedPermission, setSelectedPermission] = useState<Operation | null>(null);
  const [selectedPermissionId, setSelectedPermissionId] = useState<string>("");

  const clearPermissionSelection = useCallback(() => {
    setSelectedPermission(null);
    setSelectedPermissionId("");
  }, []);

  const selectedServiceName = useMemo(() => {
    return selectedService?.name || selectedService?.service_name || selectedService?.key || "";
  }, [selectedService]);

  const permissionById = useMemo(() => {
    const map = new Map<string, Operation>();
    (permissions || []).forEach((p: any) => {
      if (p?.id) map.set(String(p.id), p as Operation);
    });
    return map;
  }, [permissions]);

  // permissions 刷新后，尽量恢复选中权限
  useEffect(() => {
    if (!selectedPermissionId) {
      setSelectedPermission(null);
      return;
    }
    const next = permissionById.get(selectedPermissionId) || null;
    setSelectedPermission(next);
  }, [permissionById, selectedPermissionId]);

  const permissionPageActionTreeData = useMemo<TreeNodeData[]>(() => {
    const getPermissionSegment = (raw: string, level: "page" | "action"): string => {
      const parts = String(raw || "")
        .split(":")
        .map((s) => s.trim())
        .filter(Boolean);
      if (parts.length === 0) return "";
      if (level === "page") {
        return parts.length >= 2 ? parts[1] : parts[0];
      }
      // action
      return parts.length >= 3 ? parts[2] : parts[parts.length - 1];
    };

    const ops = (permissions || []) as unknown as Operation[];
    if (!ops || ops.length === 0) return [];

    // 先用 operations 的树构建（service -> page -> action），再隐藏 service 根节点，仅保留 page -> action
    const fullTree = convertPermissionsToTree(ops, undefined as any, selectedPermission?.id);
    const serviceRoot = fullTree?.[0];
    const pageNodes = (serviceRoot?.children || []) as any[];

    return pageNodes.map((pageNode) => {
      const pageCode =
        pageNode?.permissionData?.code ||
        (() => {
          const svc = pageNode?.permissionData?.serviceName || selectedServiceName;
          const page = pageNode?.permissionData?.name || "";
          return [svc, page].filter(Boolean).join(":");
        })();

      return {
        key: pageNode.id,
        title: getPermissionSegment(pageCode, "page") || pageCode,
        // 让 CompsTree 的搜索能命中 page 信息
        permissionData: { code: pageCode, page: pageNode?.permissionData?.name },
        children: (pageNode.children || []).map((leaf: any) => {
          const code = leaf?.permissionData?.code || leaf?.code || leaf?.title || "";
          const shortCode = getPermissionSegment(code, "action") || code;
          const leafId = String(leaf.id);
          const op = permissionById.get(leafId);
          const desc = (op?.description || "").trim();
          return {
            key: leaf.id,
            title: (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                  gap: 12,
                  paddingRight: 8, // 轻微兜底，避免边界贴合
                }}
              >
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {shortCode}
                </span>
                {desc ? (
                  <span style={{ flexShrink: 0, color: "#999", fontSize: 12, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {desc}
                  </span>
                ) : null}
              </div>
            ),
            isLeaf: true,
            customMenuButtonWidth: 56,
            permissionData: {
              code,
              name: op?.name,
              description: op?.description,
              page: op?.page,
            },
          };
        }),
      };
    });
  }, [permissions, selectedPermission?.id, selectedServiceName, permissionById]);

  // ============ 复用 operations CRUD ============
  const {
    createModalVisible: permissionCreateModalVisible,
    editModalVisible: permissionEditModalVisible,
    currentRecord: permissionCurrentRecord,
    handleCreate: permissionHandleCreate,
    handleEdit: permissionHandleEdit,
    handleDelete: handleDeletePermission,
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
  } = usePermissionManagement();

  const { getCreateFormConfig, getEditFormConfig } = usePermissionFormConfig();

  const reloadPermissionDetails = useCallback(
    async (id: string) => {
      // refreshPermissions 当前不返回 Promise；这里触发刷新即可
      refreshPermissions();
      setSelectedPermissionId(String(id));
    },
    [refreshPermissions]
  );

  const { handleCreateWrapper, handleEditWrapper, handleDeletePermissionClick } = usePermissionHandlers({
    handleCreate: permissionHandleCreate,
    handleEdit: permissionHandleEdit,
    handleDelete: handleDeletePermission,
    openEditModal,
    loadPermissionTree: refreshPermissions,
    clearSelection: clearPermissionSelection,
    reloadPermissionDetails,
    selectedOperation: selectedPermission,
  });

  const permissionCreateFormRef = useRef<any>(null);
  const permissionEditFormRef = useRef<any>(null);

  // 根据当前选中的服务计算树的选中节点，保证有高亮选中状态
  const selectedTreeKeys = useMemo(
    () => (selectedService ? [selectedService.id] : []),
    [selectedService]
  );

  // 使用 useServiceForm hook 管理服务表单引用
  const {
    serviceFormRef,
    setServiceFormRef,
    resetServiceForm,
    setServiceFormValues,
    submitServiceForm,
    validateServiceForm,
  } = useServiceForm();

  // 使用 useServiceDataTransformers hook 管理数据转换
  const { serviceTreeData, basicInfoData } = useServiceDataTransformers(
    serviceList,
    permissions,
    serviceDetail,
    [] // serviceTokens 不再使用
  );

  // 根据当前树结构，按服务类型分别更新 menuOrder，并支持跨类型拖动
  const handleServiceReorderFromTree = async (tree: CompsTreeNodeData[]) => {
    try {
      const groupTypeMap: Record<string, number> = {
        system: 0,
        backend: 1,
        middle: 2,
        frontend: 3,
      };

      type PendingUpdate = {
        service: ServiceRecord;
        menuOrder: number;
        serviceType: number;
      };

      const pendingUpdates: PendingUpdate[] = [];
      const updatedById = new Map<string, ServiceRecord>();

      // 按树结构计算每个类型内部的顺序，并更新目标类型
      tree.forEach((groupNode) => {
        if (!groupNode.children || !Array.isArray(groupNode.children)) return;

        const keyStr = String(groupNode.key || "");
        const match = /^group-(\w+)$/.exec(keyStr);
        const groupKey = match?.[1] || "backend";
        let targetType = groupTypeMap[groupKey] ?? 1;

        groupNode.children.forEach((child, index) => {
          const svc = (child as any).service as ServiceRecord | undefined;
          if (!svc) return;
          const nameLike =
            (svc.name ||
              svc.displayName ||
              (svc as any).display_name ||
              svc.service_name ||
              "") as string;
          const isRealSystemService = nameLike === "微服务管理";

          // 「系统服务」分组中，仅允许真正的微服务管理服务为类型 0，其它强制归为后台服务
          if (groupKey === "system" && !isRealSystemService) {
            targetType = 1;
          }
          const newOrder = index + 1;
          const currentOrder = (svc.menuOrder ?? (svc as any).menu_order ?? 0) as number;
          let currentType = 1;
          if (typeof svc.serviceType === "number") {
            currentType = svc.serviceType;
          } else if (typeof (svc as any).service_type === "number") {
            currentType = (svc as any).service_type as number;
          } else if (svc.isSystemService) {
            currentType = 0;
          }

          if (currentOrder === newOrder && currentType === targetType) {
            return;
          }

          pendingUpdates.push({
            service: svc,
            menuOrder: newOrder,
            serviceType: targetType,
          });

          // 预先构建本地更新版本
          const updated: ServiceRecord = {
            ...svc,
            menuOrder: newOrder,
            menu_order: newOrder,
            serviceType: targetType,
            service_type: targetType,
            isSystemService: targetType === 0,
          };
          updatedById.set(svc.id, updated);
        });
      });

      if (pendingUpdates.length > 0) {
        await Promise.all(
          pendingUpdates.map(({ service, menuOrder, serviceType }) =>
            ServiceApiService.updateService(service.id, {
              menuOrder,
              serviceType,
            })
          )
        );

        // 合并回完整 serviceList，保证其他字段不丢失
        const mergedServices = serviceList.map((svc) =>
          updatedById.get(svc.id) ?? svc
        );
        setServiceList(mergedServices);

        message.success("菜单顺序已更新");
      }
    } catch (error) {
      console.error("[ServiceManagement] 拖拽更新菜单顺序失败:", error);
      message.error("更新菜单顺序失败，请稍后重试");
    }
  };

  // Tab配置
  const tabItems = useMemo(() => SERVICE_DETAIL_TABS, []);

  // Tab按钮配置
  const tabButtons = useMemo(
    () =>
      getTabButtons(activeTab, {
        onEdit: () => {
          if (selectedService) {
            // 设置当前记录并打开编辑模态框
            updateState({
              currentRecord: selectedService,
              editModalVisible: true,
            });
          }
        },
        onDelete: () => {
          if (selectedService) {
            console.log("[index.tsx] 删除服务，传递完整对象:", selectedService);
            handleDelete(selectedService);
          }
        },
        onRefresh: () => {
          refreshPermissions();
        },
        onAdd: () => {
          openCreateModal();
        },
        onEditPermission: () => {
          if (selectedPermission) {
            openEditModal(selectedPermission);
          } else {
            message.warning("请先选择一个权限");
          }
        },
      }),
    [
      activeTab,
      selectedService,
      handleDelete,
      updateState,
      refreshPermissions,
      openCreateModal,
      selectedPermission,
      openEditModal,
      handleDeletePermissionClick,
    ]
  );

  // 服务列表按钮配置
  const serviceListButtons = useMemo(
    () =>
      getServiceListButtons(() => {
        updateState({ createModalVisible: true });
      }),
    [updateState]
  );

  // 当编辑模态框打开时，设置表单初始值
  useEffect(() => {
    if (editModalVisible && currentRecord) {
      const initialValues = getEditFormInitialValues();
      if (initialValues) {
        setServiceFormValues(initialValues);
      }
    }
  }, [editModalVisible, currentRecord, getEditFormInitialValues, setServiceFormValues]);

  // 页面加载后，自动选中第一个服务
  useEffect(() => {
    if (serviceList.length > 0 && !selectedService) {
      setSelectedService(serviceList[0]);
    }
  }, [serviceList, selectedService]);

  // 选中服务变化时，清空权限树选中态
  useEffect(() => {
    clearPermissionSelection();
  }, [selectedService?.id, clearPermissionSelection]);

  // 切换到权限树Tab时，确保数据为最新
  useEffect(() => {
    if (activeTab === "permissions" && selectedServiceName) {
      refreshPermissions();
    }
  }, [activeTab, selectedServiceName, refreshPermissions]);


  return (
    <div
      className="service-management-container"
      style={{ height: "100%", minHeight: 0, paddingLeft: 12, paddingRight: 12 }}
    >
      <Splitter style={{ height: "100%", minHeight: 0 }}>
        <Splitter.Panel defaultSize={400} min={320} max={720} style={{ minWidth: 0, paddingRight: 12 }}>
          {/* 左侧：服务列表 */}
          <ContainerPanel
            title={SERVICE_LIST_PANEL_CONFIG.title}
            height={SERVICE_LIST_PANEL_CONFIG.height}
            cardProps={{
              ...SERVICE_LIST_PANEL_CONFIG.cardProps,
              style: {
                ...(SERVICE_LIST_PANEL_CONFIG.cardProps?.style || {}),
                width: "100%",
                minWidth: 0,
                maxWidth: undefined,
                flexShrink: 1,
              },
            }}
            buttons={serviceListButtons}
          >
            <CompsTree
              treeData={serviceTreeData}
              loading={loading}
              showSearch={SERVICE_LIST_CONFIG.showSearch}
              showIcon={true}
              height={SERVICE_LIST_CONFIG.height}
              defaultExpandAll={true}
              selectedKeys={selectedTreeKeys}
              draggable
              onTreeDataChange={(newTree) => {
                // 根据新的树结构，按服务类型分别更新菜单顺序，并允许跨类型拖动
                handleServiceReorderFromTree(newTree);
              }}
              onSelect={(_, info) => {
                const nodeService = (info?.node as any)?.service as ServiceRecord | undefined;
                if (nodeService) {
                  setSelectedService(nodeService);
                }
              }}
            />
          </ContainerPanel>
        </Splitter.Panel>

        <Splitter.Panel style={{ minWidth: 0, paddingLeft: 12 }}>
          {/* 右侧：服务详情 */}
          <ContainerPanel
            title={SERVICE_DETAIL_PANEL_CONFIG.title}
            height={SERVICE_DETAIL_PANEL_CONFIG.height}
            cardProps={SERVICE_DETAIL_PANEL_CONFIG.cardProps}
            tabs={tabItems}
            activeTabKey={activeTab}
            onTabChange={setActiveTab}
            buttons={tabButtons}
          >
            {!selectedService ? (
              <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={EMPTY_STATE_CONFIG.noServiceSelected.style}>{EMPTY_STATE_CONFIG.noServiceSelected.text}</div>
              </div>
            ) : detailLoading ? (
              <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={EMPTY_STATE_CONFIG.loading.style}>{EMPTY_STATE_CONFIG.loading.text}</div>
              </div>
            ) : (
              <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                {/* 基本信息tab */}
                {activeTab === "basic" && (
                  <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                    <CompsDescription data={basicInfoData} columns={2} bordered={true} />
                  </div>
                )}

                {/* 授权操作tab（页面 -> 操作，支持CRUD；原“权限树”改名） */}
                {activeTab === "permissions" && (
                  <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
                    <div style={{ flex: 1, minWidth: 0, ...PERMISSION_TREE_CONTAINER_STYLE }}>
                      <CompsTree
                        treeData={permissionPageActionTreeData}
                        loading={detailLoading}
                        showSearch={true}
                        defaultExpandAll={true}
                        compact
                        height="100%"
                        customMenuButton={(node: any) => {
                          const id = String(node?.key || "");
                          const p = permissionById.get(id);
                          if (!p) return null;
                          const canEdit = node?.isLeaf !== false;
                          return (
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              {canEdit ? (
                                <Button
                                  type="text"
                                  icon={<EditOutlined />}
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditModal(p);
                                  }}
                                  style={{ padding: "0 2px", fontSize: 12 }}
                                  title="编辑该权限"
                                />
                              ) : null}
                              <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                size="small"
                                danger
                                onClick={(e) => {
                                  e.stopPropagation();
                                  Modal.confirm({
                                    title: "确认删除",
                                    content: `确定要删除权限 "${p.code || p.name}" 吗？此操作不可恢复。`,
                                    okText: "确定",
                                    cancelText: "取消",
                                    okType: "danger",
                                    onOk: async () => {
                                      await handleDeletePermissionClick(p);
                                    },
                                  });
                                }}
                                style={{ padding: "0 2px", fontSize: 12 }}
                                title="删除该权限"
                              />
                            </div>
                          );
                        }}
                        selectedKeys={selectedPermission?.id ? [selectedPermission.id] : []}
                        onSelect={(selectedKeys: React.Key[]) => {
                          const key = selectedKeys?.[0] ? String(selectedKeys[0]) : "";
                          if (!key) {
                            clearPermissionSelection();
                            return;
                          }
                          const maybePermission = permissionById.get(key);
                          if (maybePermission) {
                            setSelectedPermission(maybePermission);
                            setSelectedPermissionId(key);
                            return;
                          }
                          // 选中页面节点：不维持选中权限
                          clearPermissionSelection();
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* 管理员tab */}
                {activeTab === "admins" && (
                  <div style={{ ...ADMIN_TAB_CONTAINER_STYLE, flex: 1, minHeight: 0, overflow: "auto" }}>
                    <ContainerPanel title={ADMIN_PANEL_CONFIG.title} height={ADMIN_PANEL_CONFIG.height}>
                      {administrators.length > 0 ? (
                        <CompsList
                          listData={administrators.map((admin: any) => ({
                            id: admin.id || admin.admin_id || admin.username || String(Math.random()),
                            leftContent: admin.username || admin.name || admin.admin_name || "未知管理员",
                            rightContent: admin.email || admin.role || "",
                            admin,
                          }))}
                          loading={detailLoading}
                          showSearch={true}
                          height="auto"
                        />
                      ) : (
                        <div style={{ padding: "16px 0" }}>
                          <div style={EMPTY_STATE_CONFIG.noAdmins.style}>{EMPTY_STATE_CONFIG.noAdmins.text}</div>
                        </div>
                      )}
                    </ContainerPanel>
                    <ContainerPanel title="管理员组" height="auto">
                      {adminGroups.length > 0 ? (
                        <CompsList
                          listData={adminGroups.map((group: any) => ({
                            id: group.id || group.role_id || group.name || String(Math.random()),
                            leftContent: group.name || group.role_name || group.display_name || "未知管理员组",
                            rightContent: group.description || group.remark || "",
                            group,
                          }))}
                          loading={detailLoading}
                          showSearch={true}
                          height="auto"
                        />
                      ) : (
                        <div style={{ padding: "16px 0" }}>
                          <div style={EMPTY_STATE_CONFIG.noAdminGroups.style}>{EMPTY_STATE_CONFIG.noAdminGroups.text}</div>
                        </div>
                      )}
                    </ContainerPanel>
                  </div>
                )}
              </div>
            )}
          </ContainerPanel>
        </Splitter.Panel>
      </Splitter>

      {/* 创建权限Modal（复用 operations） */}
      <ContainerModal
        title={createModalConfig.title}
        visible={permissionCreateModalVisible}
        width={createModalConfig.width}
        height={createModalConfig.height}
        onCancel={closeCreateModal}
        footerRightButtons={[
          {
            key: "cancel",
            text: createModalConfig.cancelText,
            type: "default",
            onClick: closeCreateModal,
          },
          {
            key: "ok",
            text: createModalConfig.okText,
            type: "primary",
            onClick: () => permissionCreateFormRef.current?.submit(),
          },
        ]}
        modalProps={createModalConfig.modalProps}
      >
        <CompsForm
          formItems={getCreateFormConfig()}
          formRef={(form) => (permissionCreateFormRef.current = form)}
          initialValues={{
            serviceName: selectedServiceName || undefined,
          }}
          layout={createModalConfig.layout}
          labelCol={createModalConfig.labelCol}
          wrapperCol={createModalConfig.wrapperCol}
          formProps={{
            onFinish: async (values: any) => {
              const ok = await handleCreateWrapper(values);
              if (ok) {
                refreshPermissions();
              }
              return ok;
            },
          }}
        />
      </ContainerModal>

      {/* 编辑权限Modal（复用 operations） */}
      <ContainerModal
        title={editModalConfig.title}
        visible={permissionEditModalVisible}
        width={editModalConfig.width}
        height={editModalConfig.height}
        onCancel={closeEditModal}
        footerRightButtons={[
          {
            key: "cancel",
            text: editModalConfig.cancelText,
            type: "default",
            onClick: closeEditModal,
          },
          {
            key: "ok",
            text: editModalConfig.okText,
            type: "primary",
            onClick: () => permissionEditFormRef.current?.submit(),
          },
        ]}
        modalProps={editModalConfig.modalProps}
      >
        <CompsForm
          formItems={getEditFormConfig()}
          formRef={(form) => (permissionEditFormRef.current = form)}
          initialValues={(() => {
            const record: any = permissionCurrentRecord;
            if (!record) return undefined;
            let recordIsActive = true;
            if (typeof record?.isActive === "boolean") {
              recordIsActive = record.isActive;
            } else if (typeof record?.is_active === "boolean") {
              recordIsActive = record.is_active;
            }
            return {
              name: record.name,
              description: record.description,
              serviceName: record.serviceName || record.service_name || selectedServiceName,
              page: record.page,
              action: record.action,
              isActive: recordIsActive ? "active" : "inactive",
            };
          })()}
          layout={editModalConfig.layout}
          labelCol={editModalConfig.labelCol}
          wrapperCol={editModalConfig.wrapperCol}
          formProps={{
            onFinish: async (values: any) => {
              const ok = await handleEditWrapper(values);
              if (ok) {
                refreshPermissions();
              }
              return ok;
            },
          }}
        />
      </ContainerModal>

      {/* 创建服务Modal */}
      <ContainerModal
        title={FORM_MODAL_CONFIG.CREATE.title}
        visible={createModalVisible}
        width={FORM_MODAL_CONFIG.CREATE.width}
        height={FORM_MODAL_CONFIG.CREATE.height}
        contentPadding={FORM_MODAL_CONFIG.CREATE.contentPadding}
        onCancel={() => {
          updateState({ createModalVisible: false });
          resetServiceForm();
        }}
        footerRightButtons={createServiceModalFooterButtons({
          submitting,
          onCancel: () => {
            updateState({ createModalVisible: false });
            resetServiceForm();
          },
          onSubmit: submitServiceForm,
        })}
        modalProps={{
          destroyOnClose: true,
        }}
      >
        <CompsForm
          formItems={createServiceFormFields()}
          formRef={setServiceFormRef}
          initialValues={{
            service_type: 1,
            service_status: 1,
            is_menu_item: true,
            requires_auth: true,
            menuOrder: 0,
          }}
          formProps={{
            onFinish: async (values: any) => {
              const success = await handleCreate(values);
              if (success) {
                updateState({ createModalVisible: false });
                resetServiceForm();
                // 刷新服务详情（如果当前选中的服务是刚创建的服务）
                if (selectedService) {
                  refreshServiceDetail();
                }
              }
            },
          }}
          layout="horizontal"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
        />
      </ContainerModal>

      {/* 编辑服务Modal */}
      <ContainerModal
        title={FORM_MODAL_CONFIG.EDIT.title}
        visible={editModalVisible}
        width={FORM_MODAL_CONFIG.EDIT.width}
        height={FORM_MODAL_CONFIG.EDIT.height}
        contentPadding={FORM_MODAL_CONFIG.EDIT.contentPadding}
        onCancel={() => {
          updateState({
            editModalVisible: false,
            currentRecord: null,
          });
          resetServiceForm();
        }}
        footerRightButtons={editServiceModalFooterButtons({
          submitting,
          onCancel: () => {
            updateState({
              editModalVisible: false,
              currentRecord: null,
            });
            resetServiceForm();
          },
          onSubmit: submitServiceForm,
        })}
        modalProps={{
          destroyOnClose: true,
        }}
      >
        <CompsForm
          formItems={createServiceFormFields()}
          formRef={setServiceFormRef}
          initialValues={getEditFormInitialValues()}
          formProps={{
            onFinish: async (values: any) => {
              const success = await handleEdit(values);
              if (success) {
                updateState({
                  editModalVisible: false,
                  currentRecord: null,
                });
                resetServiceForm();
                // 刷新服务详情以显示最新数据
                if (selectedService) {
                  refreshServiceDetail();
                }
              }
            },
          }}
          layout="horizontal"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
        />
      </ContainerModal>

    </div>
  );
};

export default ServiceManagement;
